import type { ErrorStatusCode } from './errors.ts';
import type { GatewayMutationReceipt } from './gateway-mutation-receipts.ts';
import type { RoutingCacheConvergence } from './internal-tenant-routing.ts';

/**
 * Procurement multipliers above 100x are operationally invalid. This leaves
 * ample headroom for real supplier pricing while bounding malformed financial
 * inputs before they can overflow downstream multiplication.
 */
export const MAX_UPSTREAM_SKU_COST_MULTIPLIER = 100;

/** Immutable identity of one procurement-cost decision. */
export interface UpstreamSkuCostIdentity {
  readonly upstream_id: string;
  readonly upstream_channel_id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly vendor: string;
  readonly model: string;
}

export type UpstreamSkuCostSource = 'migrated_legacy' | 'operator';

/** Explicit cost multiplier. Missing rows deliberately have no implicit 1x. */
export interface UpstreamSkuCostRecord extends UpstreamSkuCostIdentity {
  readonly upstream_channel_slug: string;
  readonly cost_multiplier: number;
  readonly source: UpstreamSkuCostSource;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListUpstreamSkuCostsResponse {
  readonly costs: readonly UpstreamSkuCostRecord[];
}

/**
 * Snapshot fields are optional concurrency hints. The gateway always reloads
 * and persists the canonical identity from its marketplace catalog snapshot.
 */
export interface UpsertUpstreamSkuCostEntry {
  readonly upstream_channel_id: string;
  readonly sku_id: string;
  readonly cost_multiplier: number;
  readonly upstream_channel_slug?: string;
  readonly sku_name?: string;
  readonly vendor?: string;
  readonly model?: string;
}

export interface UpsertUpstreamSkuCostsRequest {
  readonly costs: readonly UpsertUpstreamSkuCostEntry[];
}

export interface UpstreamSkuCostUpsertEvidence extends UpstreamSkuCostIdentity {
  readonly upstream_channel_slug: string;
  readonly before_cost_multiplier: number | null;
  readonly after_cost_multiplier: number;
  readonly created: boolean;
  readonly source: 'operator';
}

export interface UpsertUpstreamSkuCostsResponse {
  /** Caller-selected operation identity, stable across committed retries. */
  readonly client_operation_id: string;
  /** True when the owner returned the already-committed receipt verbatim. */
  readonly idempotent_replay: boolean;
  readonly costs: readonly UpstreamSkuCostRecord[];
  readonly upserts: readonly UpstreamSkuCostUpsertEvidence[];
  readonly routing_revision: string;
  readonly cache: RoutingCacheConvergence;
  readonly mutation_receipt: GatewayMutationReceipt;
}

/** Structured fail-closed route/readiness evidence for one missing row. */
export interface UpstreamSkuCostReadinessErrorBody extends UpstreamSkuCostIdentity {
  readonly error: {
    readonly code: 'UPSTREAM_SKU_COST_NOT_CONFIGURED';
    readonly message: string;
    readonly retryable: false;
  };
}

export interface UpstreamSkuCostReadinessErrorResponse {
  readonly status: Extract<ErrorStatusCode, 409>;
  readonly body: UpstreamSkuCostReadinessErrorBody;
}

/**
 * `upstream_model` names the physical model the missing procurement price
 * belongs to, which a cross-model pin makes distinct from the SKU's catalog
 * `model` (epic #1327). It only shapes the operator-facing message; the body
 * carries the SKU's canonical identity either way.
 */
export function upstreamSkuCostNotConfiguredResponse(
  identity: UpstreamSkuCostIdentity,
  options: { readonly upstream_model?: string } = {},
): UpstreamSkuCostReadinessErrorResponse {
  const subject = options.upstream_model === undefined
    ? identity.sku_name
    : `${identity.sku_name} (upstream model ${options.upstream_model})`;
  return {
    status: 409,
    body: {
      error: {
        code: 'UPSTREAM_SKU_COST_NOT_CONFIGURED',
        message: `upstream SKU cost is not configured for ${subject} on channel ${identity.upstream_channel_id}`,
        retryable: false,
      },
      ...identity,
    },
  };
}

/** Structured canonical-vs-supplied identity evidence for rejected writes. */
export interface UpstreamSkuCostIdentityMismatchErrorBody {
  readonly error: {
    readonly code: 'INVALID_PAYLOAD';
    readonly message: string;
    readonly retryable: false;
  };
  readonly canonical_identity: UpstreamSkuCostIdentity & {
    readonly upstream_channel_slug: string;
  };
  readonly supplied_identity: {
    readonly upstream_channel_slug?: string;
    readonly sku_name?: string;
    readonly vendor?: string;
    readonly model?: string;
  };
}
